import React, { useState, useEffect } from "react";
import { FaBars, FaTimes } from "react-icons/fa";
import { FaRegSquare } from "react-icons/fa6";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import axios from "axios";

const TradeLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [awardCategories, setAwardCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  const menuItems = [
    { name: "DLA Headline Sponsor", path: "/dlatrade/headline-sponsor" },
    { name: "Sponsorship Opportunities", path: "/dlatrade/sponsorship" },
    { name: "DLA Trade Awards", path: "/dlatrade/awards", hasYears: true },
    { name: "A-Z Directory", path: "/dlatrade/directory" },
    { name: "Become Affiliate Member", path: "/dlatrade/become-member" },
  ];

  // Initialize axios instance
  const api = axios.create({
    baseURL: "https://backend.dlapakistan.com/api/dla-trade",
    headers: { "Content-Type": "application/json" },
    timeout: 10000,
  });

  // Fetch award categories from backend
  useEffect(() => {
    const fetchAwardCategories = async () => {
      try {
        setLoading(true);
        const response = await api.get("/trade-awards/categories/");
        if (response.data && Array.isArray(response.data)) {
          // Sort by year descending (newest first)
          const sortedCategories = [...response.data].sort((a, b) => b.year - a.year);
          setAwardCategories(sortedCategories);
        }
      } catch (error) {
        console.error("Error fetching award categories:", error);
        // If API fails, set empty array
        setAwardCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAwardCategories();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth > 1029) setIsSidebarOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobileView = windowWidth <= 1029;

  const SidebarContent = () => (
    <>
      <div className="bg-[#70B444] text-white p-3 md:p-4 text-center mb-4">
        <h3 className="m-0 text-base lg:text-lg font-semibold">Browse DLA Trade</h3>
      </div>
      <div className="space-y-1">
        {menuItems.map((item, idx) => {
          const isActive = location.pathname.includes(item.path);
          return (
            <div key={idx}>
              <div 
                onClick={() => {
                  navigate(item.path);
                  if (isMobileView && !item.hasYears) setIsSidebarOpen(false);
                }}
                className={`flex items-center py-2.5 text-[#70B444] font-medium cursor-pointer transition-all hover:bg-[rgba(112,180,68,0.1)] px-3 md:px-4 text-sm lg:text-base ${
                  isActive ? "bg-[rgba(112,180,68,0.1)] border-l-4 border-l-[#70B444]" : ""
                }`}
              >
                <FaRegSquare 
                  className={`w-3.5 h-3.5 mr-3 text-[#70B444] flex-shrink-0`} 
                />
                <span className={`${isActive ? "text-[#2d5a12] font-semibold" : "text-[#70B444]"}`}>
                  {item.name}
                </span>
              </div>
              
              {/* Dynamic Award Categories from Backend */}
              {item.hasYears && (
                <div className="bg-gray-50/50 pl-10 md:pl-12 py-3 space-y-3">
                  {loading ? (
                    <div className="text-center py-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#70B444] mx-auto"></div>
                      <p className="text-xs text-gray-500 mt-2">Loading awards...</p>
                    </div>
                  ) : awardCategories.length === 0 ? (
                    <div className="text-center py-2">
                      <p className="text-sm text-gray-500">No award categories yet</p>
                    </div>
                  ) : (
                    awardCategories.map(category => (
                      <div 
                        key={category.id} 
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/dlatrade/awards/${category.year}`);
                          if (isMobileView) setIsSidebarOpen(false);
                        }}
                        className={`text-sm cursor-pointer hover:text-[#70B444] transition-colors ${
                          location.pathname.includes(`/awards/${category.year}`) 
                            ? "font-bold text-[#2d5a12] underline" 
                            : "text-gray-600"
                        }`}
                      >
                        {category.name} {category.year}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );

  // Function to get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    
    // Check if we're on a specific award year page
    if (path.includes("/dlatrade/awards/")) {
      const yearMatch = path.match(/\/dlatrade\/awards\/(\d+)/);
      if (yearMatch) {
        const year = yearMatch[1];
        const category = awardCategories.find(cat => cat.year.toString() === year);
        return category ? `${category.name} ${year}` : `DLA Trade Awards ${year}`;
      }
      return "DLA Trade Awards";
    }
    
    // Check other menu items
    const menuItem = menuItems.find(i => path.includes(i.path));
    return menuItem?.name || "DLA Trade";
  };

  // Check if we're on a sub-page (not main DLA Trade page)
  const isSubPage = getPageTitle() !== "DLA Trade";

  return (
    <div className="w-full mx-auto px-0 py-6 mt-8 md:mt-12">
      
      {/* UPDATED: Breadcrumb Section with Black Color for Last Two */}
      <div className="max-w-[98vw] xl:max-w-[95vw] mx-auto px-4 xl:px-0">
        <nav className="text-gray-500 mb-8 text-sm mt-40 font-medium pt-2">
          {/* Home Link - green */}
          <span 
            onClick={() => navigate("/")}
            className="text-[#70B444] hover:underline cursor-pointer font-medium"
          >
            Home
          </span>
          
          <span className="text-gray-400 mx-2">»</span>
          
          {/* DLA Trade Link - BLACK, turns green on hover */}
          <span 
            onClick={() => navigate("/dlatrade")}
            className="text-black hover:text-[#70B444] hover:underline cursor-pointer font-medium uppercase tracking-wide transition-colors"
          >
            DLA Trade
          </span>
          
          {/* Current page - BLACK (no hover effect) */}
          {isSubPage && (
            <>
              <span className="text-gray-400 mx-2">»</span>
              <span className="text-black font-medium uppercase tracking-wide">
                {getPageTitle().replace("DLA Trade ", "")}
              </span>
            </>
          )}
        </nav>
      </div>

      <div className="flex w-full">
        {/* DESKTOP SIDEBAR */}
        {!isMobileView && (
          <aside className="w-72 xl:w-80 flex-shrink-0 bg-[#f7f9fc] p-4 md:p-5 rounded-lg shadow-sm h-fit sticky top-24 ml-5">
            <SidebarContent />
          </aside>
        )}

        {/* MAIN CONTENT */}
        <div className="flex-1 min-w-0 p-6 md:p-8">
          <div className="flex justify-between items-center border-b-2 border-[#70B444] pb-4 mb-8">
            <h1 className="text-2xl md:text-3xl font-extrabold text-[#2d5a12]">
              {getPageTitle()}
            </h1>
            {isMobileView && (
              <button 
                onClick={() => setIsSidebarOpen(true)}
                className="flex items-center gap-2 text-[#70B444] border border-[#70B444] rounded-lg px-3 py-2 hover:bg-[#70B444] hover:text-white transition-all text-sm font-semibold shrink-0"
              >
                <FaBars /> Menu
              </button>
            )}
          </div>
          
          <div className="min-h-[400px]">
            <Outlet context={{ awardCategories }} />
          </div>
        </div>
      </div>

      {/* MOBILE SIDEBAR */}
      {isMobileView && (
        <div
          className={`fixed top-0 right-0 h-full w-[85vw] max-w-sm bg-white z-[60] shadow-2xl transform transition-transform duration-300 ease-in-out ${
            isSidebarOpen ? "translate-x-0" : "translate-x-full"
          }`}
          style={{ top: `0`, height: `100vh` }}
        >
          <div className="flex flex-col h-full">
            <div className="p-4 border-b flex justify-between items-center bg-[#70B444] text-white">
              <span className="font-bold text-lg">Navigation</span>
              <FaTimes 
                className="cursor-pointer text-2xl" 
                onClick={() => setIsSidebarOpen(false)} 
              />
            </div>
            <div className="overflow-y-auto flex-1 p-2 bg-[#f7f9fc]">
              <SidebarContent />
            </div>
          </div>
        </div>
      )}

      {/* Overlay */}
      {isMobileView && isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[50] transition-opacity"
          style={{ top: `0` }}
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default TradeLayout;